import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCheckbox } from '../primitives/CDCheckbox';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableDate } from '../primitives/CDEditableDate';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDEditableTextarea } from '../primitives/CDEditableTextarea';

export interface ClosingInformationData {
  dateIssued?: string;
  closingDate?: string;
  disbursementDate?: string;
  settlementAgent?: string;
  fileNumber?: string;
  propertyAddress?: string;
  salePrice?: number;
}

export interface TransactionInformationData {
  borrowerName?: string;
  sellerName?: string;
  lenderName?: string;
}

export interface LoanInformationData {
  loanTerm?: string;
  purpose?: string;
  product?: string;
  loanType?: 'Conventional' | 'FHA' | 'VA' | 'Other';
  loanTypeOther?: string;
  loanIdNumber?: string;
  mic?: string;
}

export interface CDPageHeaderData {
  closingInformation: Partial<ClosingInformationData>;
  transactionInformation: Partial<TransactionInformationData>;
  loanInformation: Partial<LoanInformationData>;
}

export interface CDPageHeaderProps {
  /** Header data - new structured format */
  data?: Partial<CDPageHeaderData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<CDPageHeaderData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPageHeader - Page 1 header matching CFPB Closing Disclosure format
 *
 * Layout:
 * - Row 1: "Closing Disclosure" title (left) + italic description (right)
 * - Row 2: Three columns - Closing Information | Transaction Information | Loan Information
 */
export const CDPageHeader = React.forwardRef<HTMLDivElement, CDPageHeaderProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const closing = data?.closingInformation || {};
    const transaction = data?.transactionInformation || {};
    const loan = data?.loanInformation || {};

    // Helper to update closing information
    const updateClosing = (field: keyof ClosingInformationData, value: string | number) => {
      onChange?.({
        ...data,
        closingInformation: { ...closing, [field]: value },
      });
    };

    // Helper to update transaction information
    const updateTransaction = (field: keyof TransactionInformationData, value: string) => {
      onChange?.({
        ...data,
        transactionInformation: { ...transaction, [field]: value },
      });
    };

    // Helper to update loan information
    const updateLoan = (field: keyof LoanInformationData, value: string) => {
      onChange?.({
        ...data,
        loanInformation: { ...loan, [field]: value },
      });
    };

    // Helper to update loan type
    const updateLoanType = (type: 'Conventional' | 'FHA' | 'VA' | 'Other') => {
      onChange?.({
        ...data,
        loanInformation: { ...loan, loanType: type },
      });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        {/* Title Row */}
        <div className="flex justify-between items-start mb-4">
          <h1 className="cd-text-title">Closing Disclosure</h1>
          <p className="cd-text-body italic text-right max-w-[300px] leading-tight">
            This form is a statement of final loan terms and closing costs. Compare this document
            with your Loan Estimate.
          </p>
        </div>

        {/* Three Column Info Section */}
        <div className="grid grid-cols-3 gap-0 cd-text-body">
          {/* Closing Information */}
          <div className="pr-3">
            <div className="font-bold border-b border-cd-black pb-0.5 mb-1.5">
              Closing Information
            </div>
            <div className="space-y-0.5">
              <div className="flex">
                <span className="w-24 flex-shrink-0">Date Issued</span>
                <CDEditableDate
                  value={closing.dateIssued}
                  editable={editable}
                  onChange={(v) => updateClosing('dateIssued', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">Closing Date</span>
                <CDEditableDate
                  value={closing.closingDate}
                  editable={editable}
                  onChange={(v) => updateClosing('closingDate', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">Disbursement Date</span>
                <CDEditableDate
                  value={closing.disbursementDate}
                  editable={editable}
                  onChange={(v) => updateClosing('disbursementDate', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">Settlement Agent</span>
                <CDEditableText
                  value={closing.settlementAgent}
                  editable={editable}
                  onChange={(v) => updateClosing('settlementAgent', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">File #</span>
                <CDEditableText
                  value={closing.fileNumber}
                  editable={editable}
                  onChange={(v) => updateClosing('fileNumber', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">Property</span>
                <CDEditableTextarea
                  value={closing.propertyAddress}
                  editable={editable}
                  onChange={(v) => updateClosing('propertyAddress', v)}
                  size="body"
                  rows={2}
                />
              </div>
              <div className="flex">
                <span className="w-24 flex-shrink-0">Sale Price</span>
                <CDCurrency
                  value={closing.salePrice}
                  editable={editable}
                  onChange={(v) => updateClosing('salePrice', v)}
                />
              </div>
            </div>
          </div>

          {/* Transaction Information */}
          <div className="px-3 border-l border-cd-black">
            <div className="font-bold border-b border-cd-black pb-0.5 mb-1.5">
              Transaction Information
            </div>
            <div className="space-y-2">
              <div className="flex">
                <span className="w-16 flex-shrink-0 font-bold">Borrower</span>
                <CDEditableTextarea
                  value={transaction.borrowerName}
                  editable={editable}
                  onChange={(v) => updateTransaction('borrowerName', v)}
                  size="body"
                  rows={3}
                />
              </div>
              <div className="flex">
                <span className="w-16 flex-shrink-0 font-bold">Seller</span>
                <CDEditableTextarea
                  value={transaction.sellerName}
                  editable={editable}
                  onChange={(v) => updateTransaction('sellerName', v)}
                  size="body"
                  rows={3}
                />
              </div>
              <div className="flex">
                <span className="w-16 flex-shrink-0 font-bold">Lender</span>
                <CDEditableTextarea
                  value={transaction.lenderName}
                  editable={editable}
                  onChange={(v) => updateTransaction('lenderName', v)}
                  size="body"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Loan Information */}
          <div className="pl-3 border-l border-cd-black">
            <div className="font-bold border-b border-cd-black pb-0.5 mb-1.5">Loan Information</div>
            <div className="space-y-0.5">
              <div className="flex">
                <span className="w-20 flex-shrink-0">Loan Term</span>
                <CDEditableText
                  value={loan.loanTerm}
                  editable={editable}
                  onChange={(v) => updateLoan('loanTerm', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">Purpose</span>
                <CDEditableText
                  value={loan.purpose}
                  editable={editable}
                  onChange={(v) => updateLoan('purpose', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">Product</span>
                <CDEditableText
                  value={loan.product}
                  editable={editable}
                  onChange={(v) => updateLoan('product', v)}
                  size="body"
                />
              </div>
              <div className="flex items-start">
                <span className="w-20 flex-shrink-0 font-bold">Loan Type</span>
                <div className="flex flex-wrap gap-x-2 text-[8pt]">
                  <span className="flex items-center gap-0.5">
                    <CDCheckbox
                      checked={loan.loanType === 'Conventional'}
                      size="sm"
                      onChange={() => updateLoanType('Conventional')}
                    />
                    Conventional
                  </span>
                  <span className="flex items-center gap-0.5">
                    <CDCheckbox
                      checked={loan.loanType === 'FHA'}
                      size="sm"
                      onChange={() => updateLoanType('FHA')}
                    />
                    FHA
                  </span>
                  <span className="flex items-center gap-0.5">
                    <CDCheckbox
                      checked={loan.loanType === 'VA'}
                      size="sm"
                      onChange={() => updateLoanType('VA')}
                    />
                    VA
                  </span>
                  <span className="flex items-center gap-0.5">
                    <CDCheckbox
                      checked={loan.loanType === 'Other'}
                      size="sm"
                      onChange={() => updateLoanType('Other')}
                    />
                    {editable && loan.loanType === 'Other' ? (
                      <CDEditableText
                        value={loan.loanTypeOther}
                        editable={true}
                        onChange={(v) => updateLoan('loanTypeOther', v)}
                        size="body"
                        placeholder="Other type"
                        className="min-w-[50px]"
                      />
                    ) : (
                      <span className="border-b border-cd-black min-w-[50px] inline-block">
                        {loan.loanTypeOther || ''}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">Loan ID #</span>
                <CDEditableText
                  value={loan.loanIdNumber}
                  editable={editable}
                  onChange={(v) => updateLoan('loanIdNumber', v)}
                  size="body"
                />
              </div>
              <div className="flex">
                <span className="w-20 flex-shrink-0">MIC #</span>
                <CDEditableText
                  value={loan.mic}
                  editable={editable}
                  onChange={(v) => updateLoan('mic', v)}
                  size="body"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDPageHeader.displayName = 'CDPageHeader';
